/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the sae notify page component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import { Checkbox, GroupedField, InputOptionMode, InstitutionContext, PageLayout } from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import { PersonnelLabelComponentProps, SaeBreadcrumbs, SaeNotificationForm } from '@ngt/opms-sae';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import { faUser } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Paper } from '@mui/material';
import * as Dtos from '../../api/dtos';
import { AppSettings } from '../../AppSettings';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface ISaeNotifyPageProps {
}

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Component
 * ---------------------------------------------------------------------------------
 */

const parseBooleanToString = (input?: boolean | null) => input ? 'true' : 'false';

const convertStringToBoolean = (field?: string | null) => field === 'true';


const DEFAULT_PERSONNEL_LABEL_COMPONENT: React.FC<PersonnelLabelComponentProps> = ({
    item
}) => {
    return (
        <div>
            <FontAwesomeIcon icon={faUser as any} color={AppSettings.bctEmailAddressMatch && item.personnel?.email.toLowerCase().includes(AppSettings.bctEmailAddressMatch) ? 'green' : 'red'} style={{ marginRight: 8 }} />
            {item.personnel?.name}
        </div>
    )
}

const SaeNotifyPage: React.FunctionComponent<ISaeNotifyPageProps> = () => {
    const navigate = useNavigate();

    const params = useParams();

    const { data: institution } = React.useContext(InstitutionContext);

    const {
        permissions: [canAdministerSae],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.SaeAdminister
        ], 
        undefined, 
        undefined, 
        institution?.id
    );

    React.useEffect(() => {
        if (!permissionsLoading) {
            if (!canAdministerSae) {
                navigate(`/error/403`, { replace: true});
            }
        }
    }, [canAdministerSae, permissionsLoading, navigate]);

    const setInitialValues = React.useCallback((initialValues: Dtos.SaeNotificationForm): Dtos.SaeNotificationForm => {
        if (params.notifyMode === 'mreviewer') {
            initialValues.additionalData = {
                SendBctEmailsToSharedMailbox: 'true'
            }
        }

        return initialValues;
    }, [params]);

    return (
        <PageLayout
            breadcrumbs={<SaeBreadcrumbs />}
            heading="SAE Notification"
        >
            <Paper>

                <SaeNotificationForm
                    debug={AppSettings.debug}
                    notifyMode={params?.notifyMode!}
                    PersonnelLabelComponent={params.notifyMode === 'mreviewer' ? DEFAULT_PERSONNEL_LABEL_COMPONENT : undefined}
                    setInitialFormValues={setInitialValues as any}
                    AdditionalDataComponents={{
                        BeforeDocuments: params.notifyMode === 'mreviewer' ? () => {
                            return (
                                <GroupedField
                                    propertyPath="additionalData.SendBctEmailsToSharedMailbox"
                                    component={Checkbox}
                                    FieldGridProps={{
                                        size: {
                                            xs: 12,
                                            sm: 9,
                                            md: 9,
                                            lg: 9,
                                            xl: 9
                                        }
                                    }}
                                    ContainerGridProps={{
                                        sx: {
                                            pt: 0
                                        }
                                    }}
                                    formInputOptions={{
                                        labelMode: InputOptionMode.UseCustom
                                    }}
                                    label={
                                        <>
                                            Send emails addressed to BCT personnel (<FontAwesomeIcon icon={faUser as any} color="green" />) to the BCT shared mailbox ({AppSettings.bctMedicalReviewSharedMailbox}).
                                        </> as any
                                    }
                                    parse={parseBooleanToString as any}
                                    convert={convertStringToBoolean as any}
                                />
                            )
                        } : undefined
                    }}
                />
            </Paper>
        </PageLayout>
    );
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */
export default SaeNotifyPage;